/**
 * /driver/fleet/mileage
 * Minimum operational slice (Release B):
 *   - Shows the assigned vehicle
 *   - Shows today's mileage status + 9:00 AM JM cutoff
 *   - Lets the driver submit today's starting mileage
 */
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function DriverFleetMileage() {
  const [state, setState] = useState(null);
  const [mileage, setMileage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setErr(""); setMsg("");
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/driver/fleet/me`, { headers: authHeaders() });
      setState(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to load fleet data.");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    const n = Number(mileage);
    if (!Number.isInteger(n) || n < 0) { setErr("Enter a valid mileage (non-negative integer)."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/driver/fleet/mileage`,
        { mileage: n },
        { headers: authHeaders() },
      );
      setMsg(`Saved ${data?.log?.starting_mileage ?? n} km for today.`);
      setMileage("");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || "Submission failed."));
    } finally { setBusy(false); }
  };

  if (!state) {
    return (
      <div data-testid="driver-fleet-mileage" className="min-h-screen bg-slate-50 p-5 text-slate-600">
        {err ? <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div> : "Loading…"}
      </div>
    );
  }

  const v = state.vehicle;
  const submitted = !!state.submitted;
  const overdue = !!state.overdue;

  return (
    <div data-testid="driver-fleet-mileage" className="min-h-screen bg-slate-50 p-5">
      <div className="mx-auto max-w-md">
        <header className="mb-4">
          <h1 className="text-xl font-semibold text-slate-900">Today&apos;s mileage</h1>
          <div className="text-xs text-slate-500">Jamaica time · {state.jm_date}</div>
        </header>

        {!v ? (
          <div data-testid="driver-fleet-no-vehicle" className="rounded-xl border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">
            You have no active vehicle assigned yet. Please contact Ops.
          </div>
        ) : (
          <>
            <section data-testid="driver-fleet-vehicle-card" className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">My vehicle</div>
              <div className="mt-1 text-base font-semibold text-slate-900">
                {v.colour} {v.year} {v.make} {v.model}
              </div>
              <div className="mt-1 text-sm text-slate-600" data-testid="driver-fleet-plate">Plate: <b>{v.license_plate}</b></div>
              <div className="mt-1 text-[11px] text-slate-500">
                Ownership: {v.ownership_type}
                {state.last_mileage != null && (
                  <> · Last submitted: <b>{state.last_mileage.toLocaleString("en-JM")} km</b> ({state.last_mileage_jm_date})</>
                )}
              </div>
            </section>

            <section
              data-testid="driver-fleet-status"
              data-submitted={submitted ? "yes" : "no"}
              data-overdue={overdue ? "yes" : "no"}
              className={`mt-3 rounded-xl border p-4 ${submitted ? "border-emerald-200 bg-emerald-50" : overdue ? "border-rose-200 bg-rose-50" : "border-sky-200 bg-sky-50"}`}
            >
              {submitted ? (
                <div className="text-sm text-emerald-900">
                  ✅ Mileage submitted today: <b>{state.today_log?.starting_mileage?.toLocaleString("en-JM")} km</b>.
                  No further action needed.
                </div>
              ) : (
                <div className="text-sm text-slate-800">
                  <div className={overdue ? "font-semibold text-rose-800" : ""}>
                    {overdue ? "⚠ Overdue" : "⏰ Pending"}: {state.reminder_text}
                  </div>
                </div>
              )}
            </section>

            {!submitted && (
              <form onSubmit={submit} className="mt-4 space-y-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Starting mileage (km)</span>
                  <input
                    data-testid="driver-fleet-mileage-input"
                    type="number" inputMode="numeric" min={0}
                    className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                    placeholder="e.g. 45230"
                    value={mileage}
                    onChange={(e) => setMileage(e.target.value.replace(/\D/g, ""))}
                    required
                  />
                </label>
                {err && <div data-testid="driver-fleet-mileage-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
                {msg && <div data-testid="driver-fleet-mileage-success" className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{msg}</div>}
                <button
                  type="submit"
                  data-testid="driver-fleet-mileage-submit"
                  disabled={busy}
                  className="w-full rounded-xl bg-sky-600 px-4 py-3 text-base font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:opacity-60"
                >
                  {busy ? "Saving…" : "Submit mileage"}
                </button>
              </form>
            )}
          </>
        )}

        <p className="mt-8 text-center text-xs text-slate-400">Liantiao GO · Driver · v1</p>
      </div>
    </div>
  );
}
